import { Suspense, useMemo } from 'react'

import { clsx } from 'clsx'
import { Link, matchPath } from 'react-router-dom'

import { T, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { SquareLogo } from '@tribeplatform/react-components/Logo'
import { useRouterPath } from '@tribeplatform/react-sdk'
import { BackgroundProvider } from '@tribeplatform/react-ui-kit/BackgroundContext'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { NavVertical, Sidebar } from '@tribeplatform/react-ui-kit/Sidebar'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { ErrorBoundary } from '../../Error/ErrorBoundry.js'
import { AdminProfileMenu } from './AdminProfileMenu.js'
import { MainSidebarContent } from './MainSidebarContent.js'
import { NestedSidebarContent } from './NestedSidebarContent.js'
import {
  SidebarItem,
  sidebarsFactory,
  virtualSidebarRootFactory,
} from './sidebars.js'

const getActiveItem = (
  pathname: string,
  sidebars: ReturnType<typeof sidebarsFactory>,
) => {
  return sidebars.find(item => {
    const matched = !!matchPath(
      {
        path: item.path,
        end: item.exact || false,
        caseSensitive: item.exact || false,
      },
      pathname,
    )
    if (matched) {
      return true
    }

    return !!item?.children?.find(
      child =>
        !!matchPath(
          {
            path: child.path,
            end: child.exact || false,
            caseSensitive: item.exact || false,
          },
          pathname,
        ),
    )
  })
}

interface ActiveItemSidebarContentProps {
  activeItem: SidebarItem
  onSelect?: (item: SidebarItem) => void
}

const ActiveItemSidebarContent = ({
  activeItem,
  onSelect,
}: ActiveItemSidebarContentProps) => {
  const activeItemChildren = activeItem?.children?.length ? (
    <MainSidebarContent
      key={activeItem?.key ?? activeItem?.path}
      sidebar={activeItem}
      onSelect={onSelect}
    />
  ) : null

  const activeItemComponent = activeItem?.component ?? activeItemChildren

  if (!activeItemComponent) {
    return null
  }

  return (
    <Sidebar size="lg" className="h-screen flex-grow">
      <Sidebar.Content className="pb-3">{activeItemComponent}</Sidebar.Content>
    </Sidebar>
  )
}

export interface AdminSidebarProps {
  onSelect?: (item: SidebarItem) => void
}

export const AdminSidebar = ({ onSelect = undefined }: AdminSidebarProps) => {
  const sidebars = useStaticIntl(sidebarsFactory)
  const virtualSidebarRoot = useStaticIntl(virtualSidebarRootFactory)
  const pathname = useRouterPath()

  const activeItem = useMemo(
    () => getActiveItem(pathname, sidebars),
    [pathname, sidebars],
  )

  const isNestedSidebar = [
    '/manage/customizer/*',
    '/manage/appearance/*',
    '/manage/content/cms',
    '/manage/content/cms/:slug',
  ].some(path => matchPath({ path }, pathname))

  return (
    <div className="flex flex-1">
      <BackgroundProvider backgroundType="surface">
        <NavVertical
          size="lg"
          collapsed
          className="p-3 h-screen overflow-auto bg-surface border-line-subdued border-r justify-between"
        >
          <NavVertical.Group>
            <Tooltip placement="right">
              <Tooltip.Trigger>
                <Link
                  className={clsx(
                    'bg-surface',
                    'group flex items-center justify-center rounded-base',
                    'py-2',
                  )}
                  to="/"
                >
                  <SquareLogo size="2x" />
                </Link>
              </Tooltip.Trigger>
              <Tooltip.Panel>
                <T
                  defaultMessage="Back to {COMMUNITY}"
                  id="Generics.BackToCommunity"
                />
              </Tooltip.Panel>
            </Tooltip>
            {sidebars.map(item => {
              const active = activeItem?.path === item.path
              const navItem = (
                <NavVertical.Item
                  key={item?.key ?? item.path}
                  active={active}
                  as={Link}
                  leadingIcon={
                    typeof item.icon === 'string' ? (
                      <SvgIcon name={item.icon} size="1.5x" />
                    ) : (
                      item.icon
                    )
                  }
                  onClick={() => {
                    if (typeof onSelect === 'function') {
                      onSelect(item)
                    }
                  }}
                  to={item.href ?? item.path}
                />
              )
              if (active) {
                return navItem
              }
              return (
                <Tooltip key={item?.key ?? item.path} placement="right">
                  <Tooltip.Trigger>{navItem}</Tooltip.Trigger>
                  <Tooltip.Panel>{item.title}</Tooltip.Panel>
                </Tooltip>
              )
            })}
          </NavVertical.Group>
          <NavVertical.Group>
            <AdminProfileMenu />
          </NavVertical.Group>
        </NavVertical>
      </BackgroundProvider>
      {isNestedSidebar ? (
        <Sidebar size="lg" className="h-screen flex-grow">
          <Sidebar.Content>
            <ErrorBoundary>
              <NestedSidebarContent sidebar={virtualSidebarRoot} />
            </ErrorBoundary>
          </Sidebar.Content>
        </Sidebar>
      ) : (
        <Suspense fallback={<></>}>
          <ErrorBoundary>
            <ActiveItemSidebarContent
              activeItem={activeItem}
              onSelect={onSelect}
            />
          </ErrorBoundary>
        </Suspense>
      )}
    </div>
  )
}
